<template>
  <FiltersComponent
    :views="passedPropData.views"
    :show-views-horizontally="passedPropData.showViewsHorizontally"
    :rolling-periods="passedPropData.rollingPeriods"
    :active-filters="passedPropData.activeFilters"
    :filters="passedPropData.filters"
    :show-favorite-filters-button="passedPropData.showFavoriteFiltersButton"
    :show-unapplied-filters="passedPropData.showUnappliedFilters"
    :show-clear-cross-at-applied-filters="passedPropData.showClearCrossAtAppliedFilters"
    :fetch-favorites="fetchFavorites"
    :i18n="i18n"
    :rtl-enabled="passedPropData.isRtlEnabled"
    :lang="passedPropData.lang"
    :translations="passedPropData.translations"
    :accent-color="passedPropData.accentColor"
    :debug-events="passedPropData.debugEvents"
    :filter-invalidate="filterInvalidate"
    @rolling-changed="rollingChange"
    @reset-filters="resetFilters"
    @reset-filter="resetFilter"
    @apply-filters="afterFiltersApplied"
    @filter-invalidated="filterInvalidated"
    @apply-favorite-filters="applyFavoriteFilters"
    @delete-favorite-filters="deleteFavoriteFilters"
    @submit-favorite-filters="saveFavoriteFilters"
    @update-favorite-filters="updateFavoriteFilters"
  />
</template>

<script setup>
import { ContainerComponent, Loader } from '@/component'
import $ from 'jquery'
import { onBeforeMount, ref } from 'vue'
import { i18n } from '@/modules/filter/filterVue'
import { FiltersComponent } from 'ipsos-filters-panel/dist-lib/filters_bar.es'
import 'ipsos-filters-panel/dist-lib/style.css'

const passedPropData = ref({})

onBeforeMount(() => {
  passedPropData.value = window.filterSettings || {}
  i18n.global.locale.value = passedPropData.value.lang
})

const fetchFavorites = () => {
  return passedPropData.value.favorites;
}

const rollingChange = (rolling) => {
  Loader.create(
    ContainerComponent.getContainer('loader'),
    { absolute: true },
  )

  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.rollingChange,
    data: { rolling: rolling },
    success: () => {
      passedPropData.value.rollingPeriods.forEach((period) => {
        period.active = period.event?.data === rolling
      })
    },
    complete: () => {
      Loader.destroy(ContainerComponent.getContainer('loader'))
    },
  })
}

const resetFilters = async () => {
  Loader.create(
    ContainerComponent.getContainer('loader'),
    { absolute: true },
  )

  $.post({
    dataType: 'json',
    url: passedPropData.value.resetFilters,
    // Reset clears the filters server-side; reload the whole page (same behaviour as
    // applying filters) so the lazily-loaded Highcharts/React charts re-initialise cleanly.
    // A partial snippet redraw leaves them blank (white screen). Keep the loader visible
    // until the reload navigates away; only tear it down if the request fails.
    success: () => {
      window.location.reload()
    },
    error: () => {
      Loader.destroy(ContainerComponent.getContainer('loader'))
    },
  })
}

const resetFilter = (name) => {
  Loader.create(
    ContainerComponent.getContainer('loader'),
    { absolute: true },
  )

  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.resetFilter,
    data: { name: name },
    complete: () => {
      Loader.destroy(ContainerComponent.getContainer('loader'))
    },
  })
}

const afterFiltersApplied = (appliedFilters) => {
  Loader.create(
    ContainerComponent.getContainer('loader'),
    { absolute: true },
  )

  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.afterFiltersApplied,
    data: { filters: appliedFilters },
    // Filters are persisted server-side by handleAfterFiltersApplied(); reload the whole
    // page so lazily-loaded Highcharts/React charts re-initialise cleanly. A partial
    // snippet redraw leaves them blank (white screen). Keep the loader visible until the
    // reload navigates away; only tear it down if the request fails.
    success: () => {
      window.location.reload()
    },
    error: () => {
      Loader.destroy(ContainerComponent.getContainer('loader'))
    },
  })
}

const filterInvalidate = async (filter) => {
  return await $.post({
    dataType: 'json',
    url: passedPropData.value.filterInvalidate,
    data: { filter: filter },
  })
}

const filterInvalidated = (unApplied = []) => {
  $.post({
    dataType: 'json',
    url: passedPropData.value.getActiveFilters,
    success: (activeFilters) => {
      unApplied.length = 0
      activeFilters.forEach((filter) => {
        unApplied.push(filter)
      })
    },
  })
}

const applyFavoriteFilters = (filter) => {
  Loader.create(
    ContainerComponent.getContainer('loader'),
    { absolute: true },
  )

  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.favoritesApply,
    data: { filter: JSON.stringify(filter) },
    complete: () => {
      passedPropData.value.activeFilters = filter.filters
      Loader.destroy(ContainerComponent.getContainer('loader'))
    },
  })
}

const saveFavoriteFilters = (filter) => {
  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.favoritesSave,
    data: { filter: JSON.stringify(filter) },
    success: (newFilter) => {
      passedPropData.value.favorites.push(newFilter)
      passedPropData.value.favorites.sort((f1, f2) => f1.shared > f2.shared ? 1 : -1)
    },
  })
}

const updateFavoriteFilters = (filter) => {
  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.favoritesUpdate,
    data: { filter: JSON.stringify(filter) },
    success: (updateFilter) => {
      const index = passedPropData.value.favorites.findIndex((f) => f.id === updateFilter.id)
      passedPropData.value.favorites.splice(index, 1)
      passedPropData.value.favorites.splice(index, 0, updateFilter)
    },
  })
}

const deleteFavoriteFilters = (filter) => {
  $.nette.ajax({
    type: 'POST',
    dataType: 'json',
    url: passedPropData.value.favoritesDelete,
    data: { filter: JSON.stringify(filter) },
    complete: () => {
      passedPropData.value.favorites.splice(
        passedPropData.value.favorites.findIndex((f) => f.id === filter.id),
        1,
      )
    },
  })
}

$.nette.ext('filterHandler', {
  complete: (payload) => {
    if (!payload.redrawFilterComponents) {
      return
    }

    Object.values(payload.redrawFilterComponents).forEach(value => {
      passedPropData.value[value] = window.filterSettings[value]
    })
  },
})
</script>
